import { useState } from "react";
import { useMemo } from "react";
import { useCallback } from "react";
import React from "react";
import { DefaultRootState, useDispatch, useSelector } from "react-redux";

import { SelectItem } from "../../../../../components/Select";
import { selectCountries } from "../../../../../components/appData.slice";
import { FormElement, FormId } from "../../../../../components/enums";
import {
  FormSchema,
  SimpleFormSchemaComponents,
} from "../../../../../components/types";
import { Url } from "../../../../../router/enums";
import { provinces } from "../../../../../shared";
import { RootState } from "../../../../../store";
import {
  selectCountry,
  selectIsDefaultCountrySelected,
  selectIsNotDefaultCountrySelected,
} from "../../../../../store/HOC/genericUI.selectors";
import { postalCodeMask } from "../../../../../utilities/masks";
import { circumstancesDataForm } from "../../../Property/Incident/form";
import { selectUIData as selectIntroductoryUIData } from "../../Introductory/introductory.selectors";
import { selectIntroductoryApplicantData } from "../../Introductory/introductory.selectors";
import { FormDataInputs } from "../../Introductory/introductory.slice";
import {
  selectCircumAvailableCities,
  selectIncidentCircumstancesData,
  selectUIData,
  selectWasVehicleInMotion,
  selectWasVehicleNotInMotion,
} from "../incident.selectors";
import {
  IncidentCircumstancesFormInputs,
  setAvailableCities,
  setUIField,
  setWasDriver,
  setWasVehicleInMotion,
} from "../incident.slice";

import { ComponentId } from "./enums";

const useSetWasDriver = (): ((arg: string) => void) => {
  const dispatch = useDispatch();
  const { email, ...restData } = useSelector(selectIntroductoryApplicantData);
  const countryValidator = useSelector(
    selectCountry(selectIntroductoryUIData)("applicantphone")
  );

  return (driver: string) => {
    dispatch(
      setWasDriver({
        countryValidator,
        driver,
        ...restData,
      })
    );
  };
};

export const form: FormSchema<SimpleFormSchemaComponents> = {
  formId: FormId.INCIDENT_CIRCUMSTANCES,
  selector: selectIncidentCircumstancesData,
  redirect: Url.OC_VEHICLE_VEHICLE_OWNER,

  content: [
    {
      title: "Okoliczności zdarzenia",
      sections: [
        {
          id: "incident-circumstances-data-1",
          wrapper: { xs: 12 },
          components: [
            {
              type: FormElement.DATE_PICKER,
              id: ComponentId.CIRCUMSTANCES_DATE,
              required: true,
              isSubmitted: false,
              label: "Data zdarzenia",
            },
            {
              type: FormElement.TIME_PICKER,
              id: ComponentId.CIRCUMSTANCES_HOUR,
              required: true,
              label: "Godzina zdarzenia",
            },
          ],
        },
        {
          id: "incident-circumstances-data-2",
          wrapper: { xs: 12 },
          components: [
            {
              type: FormElement.TEXTAREA,
              id: ComponentId.CIRCUMSTANCES_PLACE,
              required: true,
              label: "Ulica / Droga / Skrzyżowanie",
              limit: 250,
              placeholder: "Proszę wpisać dokładne miejsce zdarzenia",
            },
          ],
        },
        {
          id: "incident-circumstances-data-3",
          wrapper: { xs: 12 },
          components: [
            {
              isVisible: selectIsNotDefaultCountrySelected(selectUIData)(
                "circumstances[country]"
              ),
              type: FormElement.ZIP_CODE,
              mask: postalCodeMask,
              id: ComponentId.CIRCUMSTANCES_ZIP_CODE_PL,
              label: "Kod pocztowy",
              // LPPLWEBMOB-728
              onChange: setAvailableCities,
            },
            {
              isVisible: selectIsDefaultCountrySelected(selectUIData)(
                "circumstances[country]"
              ),
              type: FormElement.INPUT,
              id: ComponentId.CIRCUMSTANCES_ZIP_CODE_GENERIC,
              label: "Kod pocztowy",
            },
            // LPPLWEBMOB-728
            {
              isVisible: selectIsNotDefaultCountrySelected(selectUIData)(
                "circumstances[country]"
              ),
              type: FormElement.SELECT_CITY,
              id: ComponentId.CIRCUMSTANCES_CITY,
              label: "Miejscowość",
              items: [],
              selectAvailableCities: selectCircumAvailableCities,
            },
            {
              isVisible: selectIsDefaultCountrySelected(selectUIData)(
                "circumstances[country]"
              ),
              type: FormElement.INPUT,
              id: ComponentId.CIRCUMSTANCES_CITY,
              label: "Miejscowość",
            },
          ],
        },
        {
          id: "incident-circumstances-data-4",
          wrapper: { xs: 12 },
          components: [
            {
              type: FormElement.AUTOCOMPLETE,
              id: ComponentId.CIRCUMSTANCES_COUNTRY,
              onChange: setUIField,
              items: selectCountries,
              label: "Kraj",
            },
            {
              type: FormElement.SELECT,
              id: ComponentId.CIRCUMSTANCES_PROVINCE,
              disabled: selectIsDefaultCountrySelected(selectUIData)(
                "circumstances[country]"
              ),
              label: "Województwo",
              items: [...provinces],
            },
          ],
        },
        {
          id: "incident-circumstances-data-5",
          wrapper: { xs: 12 },
          components: [
            {
              type: FormElement.RADIO,
              id: ComponentId.CIRCUMSTANCES_WAS_VEHICLE_IN_MOTION,
              label: "Czy Pana/Pani pojazd był w ruchu w trakcie zdarzenia?",
              required: true,
              onChange: setWasVehicleInMotion,
              items: [
                {
                  label: "tak",
                  value: "true",
                },
                {
                  label: "nie",
                  value: "false",
                },
              ],
            },
          ],
        },
        {
          id: "incident-circumstances-data-6-1",
          wrapper: { xs: 12 },
          isVisible: selectWasVehicleInMotion,
          components: [
            {
              type: FormElement.TEXTAREA,
              id: ComponentId.CIRCUMSTANCES_DESCRIPTION,
              required: true,
              label: "Opis zdarzenia",
              limit: 2000,
              placeholder:
                "Opisz jak doszło do zdarzenia (np: Sprawca nie ustąpił pierwszeństwa i przodem uderzył w lewy bok mojego pojazdu).",
            },
          ],
        },
        {
          id: "incident-circumstances-data-6-2",
          wrapper: { xs: 12 },
          isVisible: selectWasVehicleNotInMotion,
          components: [
            {
              type: FormElement.TEXTAREA,
              id: ComponentId.CIRCUMSTANCES_DESCRIPTION,
              required: true,
              label:
                "Jak doszło do zdarzenia? Jak się Pan/Pani o nim dowiedział/a?",
              limit: 2000,
              placeholder:
                "Opisz jak doszło do zdarzenia. Jeśli w pojeździe nie było nikogo - kto powiadomił o zdarzeniu?",
            },
          ],
        },
        {
          id: "incident-circumstances-data-7",
          wrapper: { xs: 12 },
          isVisible: selectWasVehicleInMotion,
          components: [
            {
              type: FormElement.RADIO,
              id: ComponentId.CIRCUMSTANCES_WAS_DRIVER,
              label: "Czy Pan/Pani kierował/a pojazdem w trakcie zdarzenia?",
              required: true,
              onChange: useSetWasDriver,
              items: [
                {
                  label: "tak",
                  value: "true",
                },
                {
                  label: "nie",
                  value: "false",
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};
